import { useState } from "react";
import type { CheckedState } from "tamagui";
import { ListFilter as ListFilterIcon } from "@tamagui/lucide-icons";
import { Body2, Button, Checkbox, hairline, Popover, Subtitle1, XStack, YStack } from "@medbillai/ui";
import { analyticsService } from "../../../lib/analytics";
import type { CaseListFilterType } from "../../../lib/analytics/types";
import type { CaseListFilters } from "../utils/types";
type CaseListFilterPopoverProps = CaseListFilters & {
  onFilterChange: (filters: CaseListFilters) => void;
};
type FilterCheckboxRowProps = {
  label: string;
  checked: CheckedState;
  onPress: () => void;
  gap?: "$sp1" | "$sp1.5";
  indented?: boolean;
};
const FilterCheckboxRow = ({
  label,
  checked,
  onPress,
  indented = false
}: FilterCheckboxRowProps) => {
  return <XStack gap="$sp1" alignItems="center" width="100%" borderRadius="$br1" paddingVertical="$sp1" paddingLeft={indented ? "$sp3" : "$sp1"} hoverStyle={{
    backgroundColor: "$surfaceSecondary"
  }} onPress={onPress} cursor="pointer" data-sentry-element="XStack" data-sentry-component="FilterCheckboxRow" data-sentry-source-file="CaseListFilterPopover.tsx">
      <Checkbox checked={checked} pointerEvents="none" data-sentry-element="Checkbox" data-sentry-source-file="CaseListFilterPopover.tsx" />
      <Body2 data-sentry-element="Body2" data-sentry-source-file="CaseListFilterPopover.tsx">{label}</Body2>
    </XStack>;
};
export const CaseListFilterPopover = ({
  showRead,
  showUnread,
  showClosed,
  onFilterChange
}: CaseListFilterPopoverProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const showOpen: CheckedState = showRead === showUnread ? showRead : "indeterminate";
  const handleFilterChange = (filterType: CaseListFilterType, value: boolean) => {
    let nextRead = showRead;
    let nextUnread = showUnread;
    let nextClosed = showClosed;
    if (filterType === "open") {
      nextRead = value;
      nextUnread = value;
    } else if (filterType === "closed") {
      nextClosed = value;
    } else if (filterType === "read") {
      nextRead = value;
    } else if (filterType === "unread") {
      nextUnread = value;
    }
    analyticsService.trackEvent("CasesFilterChanged", {
      filterType,
      enabled: value,
      resultingFilters: {
        showRead: nextRead,
        showUnread: nextUnread,
        showClosed: nextClosed
      }
    });
    onFilterChange({
      showRead: nextRead,
      showUnread: nextUnread,
      showClosed: nextClosed
    });
  };
  return <Popover size="$si4" allowFlip placement="bottom-end" open={popoverOpen} onOpenChange={setPopoverOpen} data-sentry-element="Popover" data-sentry-component="CaseListFilterPopover" data-sentry-source-file="CaseListFilterPopover.tsx">
      <Popover.Trigger asChild data-sentry-element="Popover.Trigger" data-sentry-source-file="CaseListFilterPopover.tsx">
        <Button variant="tertiary" size="small" fillWidth={false} icon={ListFilterIcon} data-sentry-element="Button" data-sentry-source-file="CaseListFilterPopover.tsx" />
      </Popover.Trigger>
      <Popover.Content borderWidth={hairline} borderColor="$strokeSecondary" zIndex="$modal" enterStyle={{
      scale: 0.85,
      opacity: 0,
      y: -6
    }} exitStyle={{
      scale: 0.85,
      opacity: 0,
      y: -6
    }} width={200} elevate transformOrigin="right top" transition="150ms easeOut" paddingHorizontal="$sp1" paddingVertical="$sp1.5" data-sentry-element="Popover.Content" data-sentry-source-file="CaseListFilterPopover.tsx">
        <YStack alignItems="flex-start" width="100%" data-sentry-element="YStack" data-sentry-source-file="CaseListFilterPopover.tsx">
          <Subtitle1 color="$textSecondary" marginBottom="$sp1.5" marginLeft="$sp1" data-sentry-element="Subtitle1" data-sentry-source-file="CaseListFilterPopover.tsx">
            Filter cases
          </Subtitle1>

          <FilterCheckboxRow label="Open" checked={showOpen} onPress={() => handleFilterChange("open", showOpen !== true)} data-sentry-element="FilterCheckboxRow" data-sentry-source-file="CaseListFilterPopover.tsx" />

          <FilterCheckboxRow label="Unread" checked={showUnread} indented={true} onPress={() => handleFilterChange("unread", !showUnread)} data-sentry-element="FilterCheckboxRow" data-sentry-source-file="CaseListFilterPopover.tsx" />

          <FilterCheckboxRow label="Read" checked={showRead} indented={true} onPress={() => handleFilterChange("read", !showRead)} data-sentry-element="FilterCheckboxRow" data-sentry-source-file="CaseListFilterPopover.tsx" />

          <FilterCheckboxRow label="Closed" checked={showClosed} onPress={() => handleFilterChange("closed", !showClosed)} data-sentry-element="FilterCheckboxRow" data-sentry-source-file="CaseListFilterPopover.tsx" />
        </YStack>
      </Popover.Content>
    </Popover>;
};